/**
 * Closed calendar-window primitives shared by Drilldown, Overview, and Dashboard.
 *
 * Services cannot import each other, so the resolver, span arithmetic, and
 * calendar check live here — next to `WORKSPACE_DATE_RANGES`, which is the
 * token vocabulary they interpret.
 */

import { z } from 'zod';

export interface DateWindow {
  startDate: string;
  endDate: string;
}

export interface DateWindowInput {
  dateRange: string;
  startDate: string;
  endDate: string;
}

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const DEFAULT_DAYS_BACK = 90;

/**
 * Inclusive-day ceiling for a client-supplied pair. Over-wide windows fall
 * back to the token. The sole literal for this cap — `MAX_DAYS_BACK` derives
 * from it (the old `[today - N, today]` shape is N+1 inclusive days).
 */
export const MAX_WINDOW_DAYS = 731;

/** Offset form of `MAX_WINDOW_DAYS` for the legacy `daysBack` field. */
export const MAX_DAYS_BACK = MAX_WINDOW_DAYS - 1;

const MILLISECONDS_PER_DAY = 86_400_000;

/**
 * Inclusive-day ceiling for a client-supplied pair. Matches the existing
 * creative-hash `daysBack` max of 730 (today-730 → today is 731 dates).
 * Anything wider falls back to the token window rather than scanning history.
 */
export const MAX_DATE_WINDOW_INCLUSIVE_DAYS = 731;

function dateRangeToDays(dateRange: string): number {
  switch (dateRange) {
    case '7d': return 7;
    case '30d': return 30;
    case '12m': return 365;
    case '90d':
    default: return DEFAULT_DAYS_BACK;
  }
}

function toYmdUtc(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** True only for a real calendar day — `2026-02-30` matches the shape but is not a date. */
export function isValidYmd(value: string): boolean {
  if (!YMD_PATTERN.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && toYmdUtc(parsed) === value;
}

/**
 * Reject a window bound before interpolation or a `@param` bind. Empty is a
 * wiring bug, not an open bound. The rejected value is deliberately left out of
 * the message: it reaches the client through the correlation status.
 */
export function assertYmd(value: string, field: string): void {
  if (!isValidYmd(value)) {
    throw new Error(`Invalid date for ${field} (expected YYYY-MM-DD)`);
  }
}

/** Inclusive span in days. `windowSpanDays({ startDate: '2026-01-01', endDate: '2026-01-01' })` is 1. */
export function windowSpanDays(window: DateWindow): number {
  const start = new Date(`${window.startDate}T00:00:00Z`).getTime();
  const end = new Date(`${window.endDate}T00:00:00Z`).getTime();
  return Math.round((end - start) / MILLISECONDS_PER_DAY) + 1;
}

/** True when both ends are real calendar days, ordered, and within the span cap. */
export function isAcceptableDateWindow(startDate: string, endDate: string): boolean {
  if (!isValidYmd(startDate) || !isValidYmd(endDate) || startDate > endDate) {
    return false;
  }
  return windowSpanDays({ startDate, endDate }) <= MAX_DATE_WINDOW_INCLUSIVE_DAYS;
}

/**
 * Resolve one closed window. The client pair wins when both dates are real,
 * ordered, and within `MAX_DATE_WINDOW_INCLUSIVE_DAYS`; anything else — absent,
 * malformed, half-supplied, reversed, over-wide — falls back to the token-derived
 * window.
 *
 * The fallback is `[today - (N - 1), today]`: N inclusive days ending today.
 */
export function resolveDateWindow(
  input: DateWindowInput,
  today: Date = new Date(),
): DateWindow {
  const { dateRange, startDate, endDate } = input;

  if (isAcceptableDateWindow(startDate, endDate)) {
    return { startDate, endDate };
  }

  return trailingWindowFromDaysBack(dateRangeToDays(dateRange), today);
}

/** True for a real, ordered pair whose inclusive span is at most `MAX_WINDOW_DAYS`. */
export function isValidWindow(window: DateWindow): boolean {
  return (
    isValidYmd(window.startDate) &&
    isValidYmd(window.endDate) &&
    window.startDate <= window.endDate &&
    windowSpanDays(window) <= MAX_WINDOW_DAYS
  );
}

/** Closed `[today - (daysBack - 1), today]` window — N inclusive days ending today. */
export function trailingWindowFromDaysBack(
  daysBack: number,
  today: Date = new Date(),
): DateWindow {
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (daysBack - 1));
  return { startDate: toYmdUtc(start), endDate: toYmdUtc(end) };
}

/** The equally-long span immediately before `window`. */
export function previousWindow(window: DateWindow): DateWindow {
  const span = windowSpanDays(window);
  const start = new Date(`${window.startDate}T00:00:00Z`);
  const prevEnd = new Date(start);
  prevEnd.setUTCDate(prevEnd.getUTCDate() - 1);
  const prevStart = new Date(start);
  prevStart.setUTCDate(prevStart.getUTCDate() - span);
  return { startDate: toYmdUtc(prevStart), endDate: toYmdUtc(prevEnd) };
}

/** Token-fallback window for the default 90-day lookback. */
export function resolveDefaultWindow(today: Date = new Date()): DateWindow {
  return trailingWindowFromDaysBack(DEFAULT_DAYS_BACK, today);
}

const ymdField = (field: 'startDate' | 'endDate') =>
  z.string().refine(isValidYmd, {
    message: `Invalid date for ${field} (expected YYYY-MM-DD)`,
  });

/** Required ordered YMD pair for create-set and persona generate. */
export const CreateSetDateWindowSchema = z
  .object({
    startDate: ymdField('startDate'),
    endDate: ymdField('endDate'),
  })
  .refine((window) => window.startDate <= window.endDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });
export type CreateSetDateWindow = z.infer<typeof CreateSetDateWindowSchema>;

/** Inclusive days from start to start+6 — the first seven calendar days of `window`. */
const FIRST_SEVEN_INCLUSIVE_OFFSET_DAYS = 6;

/**
 * Trend (7d) creatives slice: the whole pair when the span is ≤7 days, otherwise
 * the first seven inclusive days from the set start. No padding.
 */
export function derive7dEngineWindow(window: DateWindow): DateWindow {
  if (windowSpanDays(window) <= 7) {
    return window;
  }

  const start = new Date(`${window.startDate}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() + FIRST_SEVEN_INCLUSIVE_OFFSET_DAYS);
  return { startDate: window.startDate, endDate: toYmdUtc(start) };
}
